/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
// import { setCurrentUser } from "../store/actions/authActions.js";
import { NavLink } from "react-router-dom";
import { initFlowbite } from "flowbite";
import { Button, Modal } from "flowbite-react";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import instance, { fetchAllDepartments } from "../../api/api";
import { Loader } from "../../components/Loader";
import TableSearchBar from "../../components/TableSearchBar";
import { usePostHog } from '@posthog/react';

// On/off switch for a user's admin access. Disabled on your own row so an
// admin can't remove their own access by accident.
function AdminSwitch({ user, isSelf, onToggle }) {
  const isAdmin = user.role === "ADMIN";
  const name = `${user.firstName} ${user.lastName}`;
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        role="switch"
        aria-checked={isAdmin}
        aria-label={`Admin access for ${name}`}
        disabled={isSelf}
        title={isSelf ? "You can't change your own role" : isAdmin ? "Remove admin access" : "Make admin"}
        onClick={() => onToggle(user)}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-caribbeanCurrent focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${
          isAdmin ? "bg-caribbeanCurrent" : "bg-gray-300"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
            isAdmin ? "translate-x-4" : "translate-x-0.5"
          }`}
        />
      </button>
      <span className="text-xs font-medium text-gray-600">{isAdmin ? "Admin" : "Employee"}</span>
    </div>
  );
}

function Users() {
  const posthog = usePostHog();
  const currentUserId = localStorage.getItem("userId");
  // { user, nextRole } while the admin confirms a role change
  const [roleChange, setRoleChange] = useState(null);
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [roleError, setRoleError] = useState("");

  const requestRoleChange = (user) => {
    setRoleError("");
    setRoleChange({ user, nextRole: user.role === "ADMIN" ? "EMPLOYEE" : "ADMIN" });
  };

  const confirmRoleChange = async () => {
    const { user, nextRole } = roleChange;
    setIsChangingRole(true);
    setRoleError("");
    try {
      await instance.put(`/api/admin/users/user/update/${user.id}`, { role: nextRole });
      setAllUsers((list) => list.map((u) => (u.id === user.id ? { ...u, role: nextRole } : u)));
      posthog.capture('user_role_changed', { role: nextRole });
      setRoleChange(null);
    } catch (error) {
      setRoleError(error?.response?.data?.message ?? "Could not change the role. Please try again.");
    } finally {
      setIsChangingRole(false);
    }
  };
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    department: "",
    role: "EMPLOYEE"
  });
  const [updatedUser, setUpdatedUser] = useState({});
  const [currentUserDetails, setCurrentUserDetails] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const closeAddNewUserDrawerButtonRef = useRef(null);
  const closeEditUserDrawerButtonRef = useRef(null);
  const addNewUserDrawerRef = useRef(null);

  const [selectedUserId, setSelectedUserId] = useState();
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isAddNewUserDrawerOpen, setIsAddNewUserDrawerOpen] = useState(false);

  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [pageError, setPageError] = useState("");


  useEffect(() => {
    const fetchDepartments = async () => {
      setDepartments(await fetchAllDepartments());
    }
    fetchDepartments();
    posthog.capture('page_viewed', { page: 'admin_users' });
  }, [posthog])

  const editDrawerRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");

  const today = new Date();

  const createNewUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setPageError("");

    // Validation
    if (newUser.password !== newUser.confirmPassword) {
      setPageError("Passwords do not match");
      setIsSubmitting(false);
      return;
    }

    if (newUser.password.length < 8) {
      setPageError("Password must be at least 8 characters long");
      setIsSubmitting(false);
      return;
    }

    if (newUser.department === "Select Department" || !newUser.department) {
      setPageError("Department must be selected");
      setIsSubmitting(false);
      return;
    }

    const userData = {
      email: newUser.email,
      password: newUser.password,
      firstName: newUser.firstName,
      lastName: newUser.lastName,
      departmentId: newUser.department,
      confirmPassword: newUser.confirmPassword,
    };

    try {
      await instance.post("/api/auth/signup", userData);
      // Close the drawer
      if (closeAddNewUserDrawerButtonRef.current) {
        closeAddNewUserDrawerButtonRef.current.click();
      }
      posthog.capture('user_created', { role: newUser.role });
      setIsSubmitting(false);
      setNewUser({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        department: "",
        role: "EMPLOYEE"
      });
      window.location.reload();
    } catch (error) {
      posthog.capture('user_create_failed', { error: error.response?.data?.message });
      setPageError(error.response?.data?.message || "Error creating user");
      setIsSubmitting(false);
      return error;
    }
  };

  useEffect(() => {    

    const getAllUsers = async () => {

      try {
        
        setIsLoadingUsers(true);
        let response;
        if (selectedDepartment === "" || selectedDepartment === "All") {
          response = await instance.get(
            "/api/admin/users/all"
          );
          setIsLoadingUsers(false);
          console.log("The users: ", response.data.data);
          // Filter out deleted users
          const activeUsers = response.data.data.filter(user => user.isDeleted === false);
          setAllUsers(activeUsers);
        } else {
          response = await instance.get(
            `/api/admin/users/department?departmentName=${selectedDepartment}`
          );
          setIsLoadingUsers(false);
          // Filter out deleted users
          const activeUsers = response.data.data.filter(user => user.isDeleted === false);
          setAllUsers(activeUsers);
        }

        initFlowbite();
        
      } catch (error) {
        setIsLoadingUsers(false)
        console.error(error);
      }
    };

    getAllUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDepartment]);
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // Apply search filter
  const filteredUsers = searchTerm
    ? allUsers.filter((user) =>
        Object.values(user).some((value) =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : allUsers;
  
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  
  const handleEditClick = (userId, firstName, lastName, email, role, department) => {
    setSelectedUserId(userId);
    setIsEditDrawerOpen(true);
    setCurrentUserDetails({
      firstName: firstName,
      lastName: lastName,
      email: email,
      role: role,
      department: department
    })
    setUpdatedUser({
        firstName: firstName,
        lastName: lastName,
        email: email,
        role: role,
        department: department,
        departmentId: department
    })
  }

  const updateUser = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    console.log("this is the updated user: ", updatedUser)
    try {
      await instance.put(`/api/admin/users/user/update/${selectedUserId}`, {
        firstName: updatedUser.firstName,
        lastName: updatedUser.lastName,
        email: updatedUser.email,
        role: updatedUser.role,
        departmentId: updatedUser.department
      })
      if (closeEditUserDrawerButtonRef.current) {
        closeEditUserDrawerButtonRef.current.click();
      }
      posthog.capture('user_updated', { userId: selectedUserId });
      setIsSubmitting(false);
      window.location.reload();
    } catch (error) {
      setIsSubmitting(false);
      return error;
    }
  };

  const deleteUser = async (e) => {
    e.preventDefault();
    setIsDeleting(true);
 
    try {
      await instance.patch(`/api/admin/users/user/deactivate/${selectedUserId}`)
      if (closeEditUserDrawerButtonRef.current) {
        closeEditUserDrawerButtonRef.current.click();
      }
      posthog.capture('user_deactivated', { userId: selectedUserId });
      setIsDeleting(false);
      window.location.reload();
    } catch (error) {
      setIsDeleting(false);
      return error
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editDrawerRef.current && !editDrawerRef.current.contains(event.target)) {
        setIsEditDrawerOpen(false);
      }

      if (addNewUserDrawerRef.current && !addNewUserDrawerRef.current.contains(event.target)) {
        setIsAddNewUserDrawerOpen(false);
      }
    };

    if (isAddNewUserDrawerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    if (isEditDrawerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isEditDrawerOpen, isAddNewUserDrawerOpen]);

    const [openModal, setOpenModal] = useState(false);
    return (
      <>
        {/* <!-- Breadcrumb --> */}
        <nav
          className="justify-between  py-3 text-gray-700  sm:flex bg-gray-50"
          aria-label="Breadcrumb"
        >
          <ol className="inline-flex items-center mb-3 space-x-1 md:space-x-2 rtl:space-x-reverse sm:mb-0">
            <li>
              <div className="flex items-center">
                <NavLink
                  to=""
                  className=" text-sm font-medium text-gray-700 hover:text-blue-600  dark:text-gray-400 dark:hover:text-white"
                >
                  Users
                </NavLink>
              </div>
            </li>

            <li aria-current="page">
              <div className="flex items-center ">
                <svg
                  className="rtl:rotate-180 w-3 h-3 mx-1 text-gray-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 6 10"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 9 4-4-4-4"
                  />
                </svg>
                <span className="mx-1 text-sm font-medium text-gray-500 md:mx-2 dark:text-gray-400">
                  Users
                </span>
              </div>
            </li>
          </ol>
        </nav>
        <form className="flex max-w-sm my-4 justify-center items-center">
          <label
            htmlFor="countries"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            Department
          </label>
          <select
            id="countries"
            className="ml-5 bg-gray-50 border border-caribbeanCurrent  text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            onChange={(e) => setSelectedDepartment(e.target.value)}
          >
            <option>All</option>
            {departments.map((department) => (
              <option>{department.name}</option>
            ))}
          </select>
        </form>

        <div className="border-2 border-dashed rounded-lg border-gray-300 dark:border-gray-600 mb-4">
          <div className="p-5 text-lg font-semibold text-left rtl:text-right text-gray-900 bg-white dark:text-white dark:bg-gray-800 sticky top-0 z-20">
            <div className="w-full flex items-center justify-between">
              <p>Users</p>
              <div className="flex flex-row gap-4 items-center justify-center">
                <div className="">
                  <TableSearchBar
                    searchTerm={searchTerm}
                    onSearchChange={setSearchTerm}
                    placeholder="Search users..."
                  />
                </div>

                <button
                type="button"
                className="text-white bg-caribbeanCurrent hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-xs px-5 py-2.5 text-center inline-flex items-center me-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                data-drawer-target="drawer-new-user"
                data-drawer-show="drawer-new-user"
                data-drawer-placement="right"
                aria-controls="drawer-new-user"
                onClick={() => setIsAddNewUserDrawerOpen(true)}
              >
                <svg
                  className="w-4 h-4 text-white-800 dark:text-white me-2"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M16 12h4m-2 2v-4M4 18v-1a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Zm8-10a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                  />
                </svg>
                Add New User
              </button>
              </div>
              
            </div>
          </div>
          <div className="rt-wrap relative sm:rounded-lg max-h-[calc(100vh-300px)] overflow-y-auto">
            <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 bg-gray-50 dark:bg-gray-700 dark:text-gray-400 sticky top-0 z-10">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    #
                  </th>
                  <th scope="col" className="px-6 py-3">
                    First Name
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Last Name
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Department
                  </th>
                  <th scope="col" className="px-6 py-3">
                    Admin
                  </th>
                  <th scope="col" className="px-6 py-3 text-center">
                    <span className="">Actions</span>
                  </th>
                </tr>
              </thead>
              {isLoadingUsers ? (
              <tbody>
                <td colSpan="7" className="text-center">
                <div className="flex justify-center items-center h-96">
                  <Loader size={"xl"} />
                </div>
                </td>
              </tbody>
              
            ) : (
              paginatedUsers.map((user, index) => (
                <tbody key={index}>
                  <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                    >
                      {index + 1}
                    </th>
                    <td className="px-6 py-4">{user.firstName}</td>
                    <td className="px-6 py-4">{user.lastName}</td>
                    <td className="px-6 py-4">{user.email}</td>
                    <td className="px-6 py-4">{user.Department.name}</td>
                    <td className="px-6 py-4">
                      <AdminSwitch user={user} isSelf={user.id === currentUserId} onToggle={requestRoleChange} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        className="text-white hover:text-white border border-caribbeanCurrent hover:bg-caribbeanCurrent focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-xs px-11 py-2 text-center inline-flex me-2 mb-2 dark:border-blue-500 dark:text-blue-500 dark:hover:text-white dark:hover:bg-caribbeanCurrent dark:focus:ring-blue-800 bg-caribbeanCurrent hover:bg-blue-800 focus:ring-blue-300 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
                        data-drawer-target="drawer-edit-user"
                        data-drawer-show="drawer-edit-user"
                        data-drawer-placement="right"
                        aria-controls="drawer-edit-user"
                        onClick={() => handleEditClick(user.id, user.firstName, user.lastName, user.email, user.role, user.Department.id)}
                      >
                        <svg
                          className="w-4 h-4 text-white-800 dark:text-white me-2"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="m14.304 4.844 2.852 2.852M7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-4.5m2.409-9.91a2.017 2.017 0 0 1 0 2.853l-6.844 6.844L8 14l.713-3.565 6.844-6.844a2.015 2.015 0 0 1 2.852 0Z"
                          />
                        </svg>
                        Edit
                      </button>
                    </td>
                  </tr>
                </tbody>
              ))
            )}
            </table>

            {/* Pagination */}
      <div className="mt-4 pl-10 pr-10 pb-5">
        <nav aria-label="Page navigation">
          <ul className="flex justify-between">
            <li className="flex flex-row items-center">
              <div>
                <p>Show rows per page</p>
              </div>
              <div>
                <select className="ml-2 bg-gray-50 border border-caribbeanCurrent text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                onChange={(e) => setItemsPerPage(e.target.value)}>
                 <option>25</option>
                <option>50</option>
                <option>100</option>
                </select>
              </div>
            </li>
            <li className="flex flex-row gap-1 items-center text-gray-500">
              <div>
                <p>{currentPage} of {totalPages === 0 ? 1 : totalPages}</p>
              </div>
            <div>
            <button
                className="px-3 py-2 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg rounded-r-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                {"<"}
              </button>
            </div>
              <div>
              <button
                className="px-3 py-2 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg rounded-l-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                {">"}
              </button>
              </div>
            </li>
          </ul>
        </nav>
      </div>
          </div>
        </div>

        {/* drawers */}

        {/* new user */}
        <div
          id="drawer-new-user"
          ref={addNewUserDrawerRef}
          className={`fixed top-0 right-0 z-50 h-screen p-4 overflow-y-auto transition-transform ${
            isAddNewUserDrawerOpen ? 'translate-x-0' : 'translate-x-full'
          } bg-white w-full md:w-1/4 dark:bg-gray-800`}
          tabIndex="-1"
          aria-labelledby="drawer-new-user-label"
        >
          <h5
            id="drawer-label"
            className="inline-flex items-center mb-6 text-base font-semibold text-gray-500  dark:text-gray-400"
          >
            Add New User
          </h5>
          <button
            type="button"
            data-drawer-hide="drawer-new-user"
            aria-controls="drawer-new-user"
            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 absolute top-2.5 end-2.5 inline-flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white"
            ref={closeAddNewUserDrawerButtonRef}
            onClick={() => setIsAddNewUserDrawerOpen(false)}
          >
            <svg
              className="w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
            <span className="sr-only">Close menu</span>
          </button>
          <form onSubmit={createNewUser} className="border-2 border-dashed rounded-lg border-gray-300 dark:border-gray-600 h-full mb-4 p-4">
            <div className="grid md:grid-cols-2 md:gap-6">
              <div className="relative z-0 w-full mb-5 group">
                <label
                  htmlFor="firstName"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-caribbeanCurrent focus:border-caribbeanCurrent block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-caribbeanCurrent dark:focus:border-caribbeanCurrent"
                  placeholder=""
                  required
                  value={newUser.firstName}
                  onChange={(e) => {
                    setNewUser((prevState) => ({
                      ...prevState,
                      firstName: e.target.value,
                    }));
                  }}
                />
              </div>
              <div className="relative z-0 w-full mb-5 group">
                <label
                  htmlFor="lastName"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-caribbeanCurrent focus:border-caribbeanCurrent block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-caribbeanCurrent dark:focus:border-caribbeanCurrent"
                  placeholder=""
                  required
                  value={newUser.lastName}
                  onChange={(e) => {
                    setNewUser((prevState) => ({
                      ...prevState,
                      lastName: e.target.value,
                    }));
                  }}
                />
              </div>
            </div>
            <div className="mb-5">
              <div className="relative z-0 w-full mb-5 group">
                <label
                  htmlFor="department"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Department
                </label>
                <select
                  id="department"
                  value={newUser.department}
                  onChange={(e) => {
                    setNewUser((prevState) => ({
                      ...prevState,
                      department: e.target.value,
                    }));
                  }}
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-caribbeanCurrent focus:border-caribbeanCurrent block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-caribbeanCurrent dark:focus:border-caribbeanCurrent"
                  required
                >
                  <option value="">Select Department</option>
                  {departments &&
                    departments.map((department) => (
                      <option key={department.id} value={department.id}>
                        {department.name}
                      </option>
                    ))}
                </select>
              </div>
              <label
                htmlFor="email"
                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-caribbeanCurrent focus:border-caribbeanCurrent block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-caribbeanCurrent dark:focus:border-caribbeanCurrent"
                placeholder="name@riskapp.com"
                required
                value={newUser.email}
                onChange={(e) => {
                  setNewUser((prevState) => ({
                    ...prevState,
                    email: e.target.value,
                  }));
                }}
              />
            </div>

            <div className="mb-5">
              <label
                htmlFor="password"
                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-caribbeanCurrent focus:border-caribbeanCurrent block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-caribbeanCurrent dark:focus:border-caribbeanCurrent"
                required
                value={newUser.password}
                onChange={(e) => {
                  setNewUser((prevState) => ({
                    ...prevState,
                    password: e.target.value,
                  }));
                }}
              />
            </div>

            <div className="mb-5">
              <label
                htmlFor="confirmPassword"
                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-caribbeanCurrent focus:border-caribbeanCurrent block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-caribbeanCurrent dark:focus:border-caribbeanCurrent"
                required
                value={newUser.confirmPassword}
                onChange={(e) => {
                  setNewUser((prevState) => ({
                    ...prevState,
                    confirmPassword: e.target.value,
                  }));
                }}
              />
            </div>

            <div className="flex gap-4">
              <button
                type="submit"
                className="text-white bg-caribbeanCurrent hover:bg-blue-800 w-full focus:ring-4 focus:ring-caribbeanCurrent font-medium rounded-lg text-sm px-5 py-2.5 mb-2 dark:bg-caribbeanCurrent dark:hover:bg-caribbeanCurrent focus:outline-none dark:focus:ring-caribbeanCurrent block"
              >
                {isSubmitting ? <Loader size="sm" /> : "Create User"}
              </button>

              <button
                onClick={(e) => {
                  e.preventDefault();
                  setIsAddNewUserDrawerOpen(false);
                }}
                className="text-black border-2 border-caribbeanCurrent hover:text-white hover:bg-caribbeanCurrent w-full focus:ring-4 focus:ring-caribbeanCurrent font-medium rounded-lg text-sm px-5 py-2.5 mb-2 dark:bg-caribbeanCurrent dark:hover:bg-caribbeanCurrent focus:outline-none dark:focus:ring-caribbeanCurrent block"
              >
                Cancel
              </button>
            </div>
            {pageError && <p className="text-red-500 mt-2">{pageError}</p>}
          </form>
        </div>

        {/* edit user */}
        {allUsers.length > 0 && (
          <div
          id="drawer-edit-user"
          ref={editDrawerRef}
          className={`fixed top-0 right-0 z-50 h-screen p-4 overflow-y-auto transition-transform ${
            isEditDrawerOpen ? 'translate-x-0' : 'translate-x-full'
          } bg-white w-full md:w-1/4 dark:bg-gray-800`}
          tabIndex="-2"
          aria-labelledby="drawer-edit-user-label"
        >
          <h5
            id="drawer-label"
            className="inline-flex items-center mb-6 text-base font-semibold text-gray-500  dark:text-gray-400"
          >
            Edit User
          </h5>
          <button
            type="button"
            data-drawer-hide="drawer-new-user"
            aria-controls="drawer-new-user"
            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 absolute top-2.5 end-2.5 inline-flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white"
            ref={closeEditUserDrawerButtonRef}
            onClick={() => setIsEditDrawerOpen(false)}
          >
            <svg
              className="w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
            <span className="sr-only">Close menu</span>
          </button>
          <form key={selectedUserId} onSubmit={updateUser} className="border-2 border-dashed rounded-lg border-gray-300 dark:border-gray-600 h-full mb-4 p-4">
            <div className="mb-6">
              <label
                htmlFor="firstName"
                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >
                First Name
              </label>
              <input
              type="text"
              id="firstName"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="First name goes here"
              onChange={(e) => {
                setUpdatedUser((prevState) => ({
                  ...prevState,
                  firstName: e.target.value,
                }));
              }}
              defaultValue={updatedUser.firstName || ""}
            />
            </div>

            <div className="mb-6">
              <label
                htmlFor="lastName"
                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >
                Last Name
              </label>
              <input
              type="text"
              id="lastName"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Last name goes here"
              onChange={(e) => {
                setUpdatedUser((prevState) => ({
                  ...prevState,
                  lastName: e.target.value,
                }));
              }}
              defaultValue={updatedUser.lastName || ""}
            />
            </div>

            <div className="mb-6">
              <label
                htmlFor="email"
                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >
                Email Address
              </label>
              <input
              type="text"
              id="email"
              className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              placeholder="Email goes here"
              onChange={(e) => {
                setUpdatedUser((prevState) => ({
                  ...prevState,
                  email: e.target.value,
                }));
              }}
              defaultValue={updatedUser.email || ""}
            />
            </div>

            <div className="mb-6">
              <label
                htmlFor="department"
                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >
               Department
              </label>
              <select
                        id="department"
                        value={updatedUser.department || ""}
                        onChange={(e) => {
                          setUpdatedUser((prevState) => ({
                            ...prevState,
                            department: e.target.value,
                          }));
                        }}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-caribbeanCurrent focus:border-caribbeanCurrent block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-caribbeanCurrent dark:focus:border-caribbeanCurrent"
                      >
                        <option>Select Department</option>
            {departments && departments.map((department) => (
              <option key={department.id} value={department.id}>{department.name}</option>
            ))}
          </select>
            </div>


            <div className="mb-6">
              <label
                htmlFor="role"
                className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
              >
                Role
              </label>
              <select
                id="role"
                className=" bg-gray-50 border border-caribbeanCurrent  text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                onChange={(e) => {
                  setUpdatedUser((prevState) => ({
                    ...prevState,
                    role: e.target.value,
                  }));
                }}
                value={updatedUser.role || ""}
              >
                <option>EMPLOYEE</option>
                  <option>ADMIN</option>
              </select>
            </div>
            <button
              type="submit"
              className="text-white bg-caribbeanCurrent hover:bg-blue-800 w-full focus:ring-4 focus:ring-caribbeanCurrent font-medium rounded-lg text-sm px-5 py-2.5 mb-2 dark:bg-caribbeanCurrent dark:hover:bg-caribbeanCurrent focus:outline-none dark:focus:ring-caribbeanCurrent block"
            >
              {isSubmitting ? <Loader size="sm" /> : "Save User"}
            </button>
            <button
              type="submit"
              className="text-caribbeanCurrent w-full border border-caribbeanCurrent hover:bg-caribbeanCurrent focus:ring-4 focus:outline-none focus:ring-caribbeanCurrent font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2 dark:border-caribbeanCurrent dark:text-caribbeanCurrent hover:text-white dark:hover:text-white dark:hover:bg-caribbeanCurrent dark:focus:ring-caribbeanCurrent"
              onClick={(e) => {
                e.preventDefault();
                setOpenModal(true);
              }}
            >
              {isDeleting ? <Loader size="sm" /> : "Delete User"}
            </button>
            <Modal show={openModal} size="md" onClose={() => setOpenModal(false)} popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <div className="gap-0 mb-4">
                <h3 className="text-lg font-normal text-gray-500 dark:text-gray-400">
                Are you sure you want to delete this user?
                </h3>
                <h4 className="text-gray-500 dark:text-gray-400"><span className="font-bold text-black">Note:</span>{" "}You can't undo this action</h4>
            </div>
            <div className="flex justify-center gap-4">
              <Button className="bg-caribbeanCurrent" onClick={deleteUser}>
              {isDeleting ? <Loader size="sm" /> : "Delete User"}
              </Button>
              <Button color="gray" onClick={() => setOpenModal(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
          </form>
        </div>
        )}

      {/* Confirm admin access change */}
      <Modal show={!!roleChange} size="md" onClose={() => !isChangingRole && setRoleChange(null)} popup>
        <Modal.Header />
        <Modal.Body>
          {roleChange && (
            <div className="text-center">
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {roleChange.nextRole === "ADMIN"
                  ? `Make ${roleChange.user.firstName} ${roleChange.user.lastName} an admin?`
                  : `Remove admin access from ${roleChange.user.firstName} ${roleChange.user.lastName}?`}
              </h3>
              <p className="mb-2 text-sm text-gray-500">
                {roleChange.nextRole === "ADMIN"
                  ? "Admins can manage users and settings and move risks through every stage."
                  : "They will only be able to enter, rate and comment on risks as an employee."}
              </p>
              <p className="mb-5 text-xs text-gray-400">Their access changes straight away. Their menus update the next time they sign in.</p>
              {roleError && <p className="mb-4 text-sm text-red-600">{roleError}</p>}
              <div className="flex justify-center gap-3">
                <Button color="gray" onClick={() => setRoleChange(null)} disabled={isChangingRole}>
                  Cancel
                </Button>
                <button
                  type="button"
                  onClick={confirmRoleChange}
                  disabled={isChangingRole}
                  className="rounded-lg bg-caribbeanCurrent px-5 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                >
                  {isChangingRole
                    ? "Saving…"
                    : roleChange.nextRole === "ADMIN" ? "Make admin" : "Remove admin access"}
                </button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
      </>
    );
//   }
}

const mapStateToProps = (state) => {
  return {
    // user: state.userData.user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    // setUser: (user) => dispatch(setCurrentUser(user)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Users);
